import { useEffect, useState } from 'react';
import { useScopedUrl } from '../nav/scopeOrg';
import { builtinIdleNotice, type Device, type Notice } from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { useCalibrationStore } from '../store/calibration';
import { NoticeEditorModal } from './NoticeEditorModal';
import { Button, Panel } from './ui';
import { describeApiError, toast } from './toast';

/** Device-specific idle content is available independently of power control. */
export function DeviceIdleCard({ device, className, readOnly = false, onChanged }: {
  device: Device;
  className?: string;
  readOnly?: boolean;
  onChanged?: () => void;
}) {
  const scoped = useScopedUrl();
  // 기기별 대기 화면 — 슬라이드 라이브러리와 분리해 기기 설정에 붙는다(2026-09-18).
  // 저장 여부는 기기 레코드(hasIdleNotice)로 알고, 내용은 편집을 열 때 읽는다.
  const [hasIdleNotice, setHasIdleNotice] = useState(device.hasIdleNotice ?? false);
  const [idleEditor, setIdleEditor] = useState<Notice | null>(null);
  const [idleLoading, setIdleLoading] = useState(false);
  const mediaList = useCalibrationStore(s => s.mediaList);
  const loadMedia = useCalibrationStore(s => s.loadMedia);
  const idleEndpoint = scoped(`${API_BASE}/api/group-workflow/idle-content/device/${encodeURIComponent(device.id)}`);
  useEffect(() => { setHasIdleNotice(device.hasIdleNotice ?? false); }, [device.id, device.hasIdleNotice]);
  /** 마지막으로 저장된 대기 화면부터 편집한다 — 기기 전용 → 조직 게시본 → 기본 화면 순. */
  const openIdleEditor = async () => {
    setIdleLoading(true);
    try {
      const portrait = device.orientation === 'portrait';
      let base: Notice | null = null;
      if (hasIdleNotice) {
        const response = await fetch(idleEndpoint, { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        base = ((await response.json()) as { notice: Notice | null }).notice;
      }
      if (!base) {
        const response = await fetch(scoped(`${API_BASE}/api/group-workflow/idle-content?orientation=${portrait ? 'portrait' : 'landscape'}`), { credentials: 'include' });
        if (response.ok) base = ((await response.json()) as { notice: Notice | null }).notice;
      }
      if (!mediaList.length) await loadMedia().catch(() => undefined);
      // 편집기는 id 가 있으면 라이브러리 갱신으로 여기므로 id 를 비운 사본을 넘긴다.
      const source = base ?? builtinIdleNotice(portrait);
      setIdleEditor({ ...source, id: '', name: source.name || '기기 대기 화면', canWrite: true } as Notice);
    } catch (e) {
      toast(`대기 화면을 불러오지 못했습니다 — ${describeApiError(e)}`, 'error');
    } finally { setIdleLoading(false); }
  };
  const putIdleNotice = async (notice: unknown) => {
    const response = await fetch(idleEndpoint, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notice }) });
    if (!response.ok) { const error = await response.json().catch(() => ({})) as { error?: string }; throw new Error(error.error || `HTTP ${response.status}`); }
    setHasIdleNotice(!!((await response.json()) as { notice: Notice | null }).notice);
    onChanged?.();
  };

  return (
    <Panel title="대기 화면" className={className ?? ''} bodyClassName="p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-[14px] text-text">재생할 편성이 없을 때 표시할 화면</p>
          <p className="text-[12px] text-text-dimmer">이 기기에만 적용됩니다. 조직 공통 화면으로 되돌릴 수 있습니다.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button className="py-1.5 text-[13px]" disabled={readOnly || idleLoading}
            title="이 기기에만 적용되는 대기 화면을 슬라이드 편집기로 고칩니다. 슬라이드 목록에는 남지 않습니다."
            onClick={() => void openIdleEditor()}>
            {idleLoading ? '불러오는 중…' : '대기 화면 편집'}
          </Button>
          {hasIdleNotice && <>
            <span className="text-[12px] text-text-dimmer">기기 전용 화면 사용 중</span>
            <button type="button" className="text-[12px] text-text-dim underline-offset-2 hover:underline disabled:opacity-50" disabled={readOnly}
              onClick={() => void putIdleNotice(null).then(() => toast('조직 공통 대기 화면으로 되돌렸습니다.', 'success'), e => toast(describeApiError(e), 'error'))}>
              조직 공통으로 되돌리기
            </button>
          </>}
        </div>
      </div>
      {idleEditor && !readOnly && (
        <NoticeEditorModal notice={idleEditor} mediaList={mediaList} onClose={() => setIdleEditor(null)}
          saveTarget={{ label: `${device.name} 대기 화면`, save: putIdleNotice }} />
      )}
    </Panel>
  );
}
